import React from "react";
import T from "prop-types";
import styled from "styled-components";

const TableWrapper = styled.table`
  border-collapse: collapse;

  tr {
    display: flex;
    border: 1px solid ${({ theme }) => theme.palette.blue.dark};
    align-items: center;
    color: ${({ theme }) => theme.palette.navy.medium};
  }

  thead {
    tr {
      margin-bottom: 21px;
    }
  }

  tbody {
    tr {
      &:nth-child(even) {
        background: ${({ theme }) => theme.palette.blue.light};
      }

      &:hover {
        td {
          background: ${({ theme }) => theme.palette.blue.medium};
        }
      }

      margin-top: -1px;
    }
  }
`;

export const Th = styled.th`
  &:hover {
    text-decoration: underline;
    background: ${({ theme }) => theme.palette.blue.medium};
    border-left: 1px solid ${({ theme }) => theme.palette.blue.dark};
    border-right: 1px solid ${({ theme }) => theme.palette.blue.dark};
    color: ${({ theme }) => theme.palette.navy.dark};
  }

  &:first-child {
    width: ${(props) => (props.fullWidthFirstColumn ? "140px" : "60px")};
    padding: 10px 10px 10px 20px;
    border-left: 1px solid transparent;
  }

  &:last-child {
    border-right: 1px solid transparent;
  }

  font-weight: ${({ theme }) => theme.typography.bold};
  cursor: pointer;
  font-size: 14px;
  line-height: 24px;
  width: 140px;
  height: 94px;
  text-align: left;
  padding: 10px;
  margin: 0;
  border: 1px solid transparent;
  letter-spacing: 0;

  ${(props) =>
    props.selected &&
    `
    text-decoration: underline;
    border-left: 1px solid ${props.theme.palette.blue.dark};
    border-right: 1px solid ${props.theme.palette.blue.dark};
    background: ${props.theme.palette.blue.medium};
  `};
`;

export const Td = styled.td`
  &:first-child {
    width: ${(props) => (props.fullWidthFirstColumn ? "140px" : "60px")};
    padding: 10px 10px 10px 20px;
    border-left: 1px solid transparent;
  }

  &:last-child {
    border-right: 1px solid transparent;
  }

  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 24px;
  width: 140px;
  height: 74px;
  text-align: left;
  padding: 10px;
  margin: 0;
  letter-spacing: 0;

  ${(props) =>
    props.selected &&
    `
    border-left: 1px solid ${props.theme.palette.blue.dark};
    border-right: 1px solid ${props.theme.palette.blue.dark};
    background: ${props.theme.palette.blue.medium};
  `};
`;

const SortingArrowWrapper = styled.span`
  font-size: 7px;
  margin-left: 2px;
  display: inline-block;
`;

const SortingArrow = ({ ascending }) => (
  <SortingArrowWrapper> {ascending ? "▼" : "▲"}</SortingArrowWrapper>
);

SortingArrow.propTypes = {
  ascending: T.bool,
};

const ascendingSortFunc = (accessor) => (a, b) => {
  if (a[accessor] < b[accessor]) return -1;
  if (b[accessor] < a[accessor]) return 1;
  return 0;
};

const descendingSortFunc = (accessor) => (a, b) =>
  ascendingSortFunc(accessor)(b, a);

class Table extends React.Component {
  constructor(props) {
    super(props);

    const rows = props.initialSortColumn
      ? this.sortByAccessor(
          props.initialSortColumn,
          [...props.data],
          props.ascending,
        )
      : [...props.data];

    this.state = {
      rows,
      ascending: props.ascending,
      selectedColumn: props.selectedColumn,
    };
  }

  onColumnClick = (accessor) => {
    const { ascending, rows } = this.state;
    this.setState({
      selectedColumn: accessor,
      rows: this.sortByAccessor(accessor, rows, !ascending),
      ascending: !ascending,
    });
  };

  sortByAccessor = (accessor, rows, ascending) =>
    rows.sort(
      ascending ? ascendingSortFunc(accessor) : descendingSortFunc(accessor),
    );

  render() {
    const { columns, fullWidthFirstColumn } = this.props;
    const { ascending, rows, selectedColumn } = this.state;
    return (
      <TableWrapper>
        <thead>
          <tr>
            {columns.map(({ name, accessor }) => (
              <Th
                selected={selectedColumn === accessor}
                fullWidthFirstColumn={fullWidthFirstColumn}
                onClick={() => this.onColumnClick(accessor)}
                key={accessor}
              >
                {name}
                {selectedColumn === accessor && (
                  <SortingArrow ascending={ascending} />
                )}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.rank}>
              {columns.map(({ accessor, formatter = (value) => value }) => (
                <Td
                  selected={selectedColumn === accessor}
                  fullWidthFirstColumn={fullWidthFirstColumn}
                  even={index % 2 === 0}
                  key={`${row.rank}-${accessor}`}
                >
                  {formatter(row[accessor])}
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </TableWrapper>
    );
  }
}

const columnsValidator = (
  propValue,
  index,
  componentName,
  location,
  propFullName,
  ...rest
) => {
  if (index === 0 && propValue[0].accessor !== "rank") {
    return new Error(
      `
        Invalid prop '${propFullName}' supplied to '${componentName}'.
        First column must have accessor 'rank'.
      `,
    );
  }

  return T.shape({
    name: T.oneOfType([T.element, T.string]).isRequired,
    accessor: T.string.isRequired,
    formatter: T.func,
  })(propValue, index, componentName, location, propFullName, ...rest);
};

Table.propTypes = {
  initialSortColumn: T.string,
  ascending: T.bool,
  columns: T.arrayOf(columnsValidator),
  selectedColumn: T.string,
  data: T.arrayOf(
    T.shape({
      rank: T.number,
    }),
  ),
  fullWidthFirstColumn: T.string,
};

export { Table };
